import type { StepVideo } from "./task-video-client";
export type MarketCode = "CN" | "VN";
export const MARKET_CONFIG: Record<
  MarketCode,
  { label: string; currency: string; timeZone: string; enabled: boolean }
> = {
  CN: {
    label: "中国大陆",
    currency: "CNY",
    timeZone: "Asia/Shanghai",
    enabled: true,
  },
  VN: {
    label: "越南",
    currency: "VND",
    timeZone: "Asia/Ho_Chi_Minh",
    enabled: false,
  },
};
export type TaskStatus =
  | "draft"
  | "pending_review"
  | "approved"
  | "rejected"
  | "paused"
  | "offline";
export type TaskType = "normal" | "other_normal" | "download" | "message_send";
export interface Agent {
  promotion_require_review?: boolean;
  id: number;
  name: string;
  username: string;
  must_change_password: boolean;
  market_id: MarketCode;
  currency: string;
  login_disabled?: boolean;
  publish_restricted?: boolean;
  business_frozen?: boolean;
  allowed_task_types?: TaskType[];
  advertising_enabled: boolean;
}
export interface TaskStep {
 videos?: StepVideo[];
  id: string;
  order: number;
  title: string;
  description: string;
  images: string[];
  links: { url: string; label: string }[];
}
export interface TaskPayload {
  title: string;
  description: string;
  description_title?: string;
  category: string;
  task_type: TaskType;
  platform: "ios" | "web" | "all";
  market_id: "CN";
  country_code: "CN";
  currency: "CNY";
  reward_amount_minor: number;
  max_assignments: number;
  assignment_timeout_seconds: number;
  require_review: boolean;
  icon_url?: string;
  tags: string[];
  rules: string;
  rules_title?: string;
  review_rules: string;
  review_rules_title?: string;
  steps_mode?: "default" | "custom";
  steps: TaskStep[];
  download_app?: {
    id: number;
    bundle_id: string;
    name: string;
    version: string;
    price: number;
    formatted_price?: string;
    artwork_url: string;
    developer_name: string;
    description?: string;
    genre?: string;
    file_size_bytes?: number;
    rating?: number;
    rating_count?: number;
    content_rating?: string;
    screenshot_urls?: string[];
    search_keywords: string[];
    search_keyword_quotas: {
      keyword: string;
      target_count: number;
      completed_count: number;
    }[];
  };
  message_config?: {
    import_id?: number;
    import_count?: number;
    campaign_type?: string;
    authorization_reference?: string;
    manual_approval_required: boolean;
  };
  required_screenshots?: {
    key: string;
    title: string;
    description: string;
    required: boolean;
  }[];
  completion_screenshot_count: number;
  submission_requirements: string;
  remarks: string;
  start_at: string | null;
  end_at: string | null;
}
export type ExecutionStatus = TaskStatus | "active" | "ended" | "settled";
export interface TaskExecution {
  completed_count: number; remaining_count: number; in_flight_count: number; pending_review_count: number; reward_credited_count: number; reward_pending_count: number; max_assignments: number; settlement_scope: "member_task_reward";
}
export const executionLabels: Record<ExecutionStatus, string> = { draft: "草稿", pending_review: "待审核", rejected: "已驳回", approved: "待开始", active: "进行中", paused: "已暂停", ended: "已结束", settled: "奖励已发放", offline: "已下架" };
export interface Task {
  business?: "promotion" | "sms";
  execution_business?: "promotion" | "sms";
  asset_urls?: Record<string, string>;
  execution_status?: ExecutionStatus;
  formal_closed?: boolean;
  execution?: TaskExecution;
  id: number;
  agent_id: number;
  version: number;
  current_revision_id: number;
  formal_task_id?: number;
  status: TaskStatus;
  payload: TaskPayload;
  created_at: string;
  updated_at: string;
}
export interface Revision {
  asset_urls?: Record<string, string>;
  id: number;
  revision_no?: number;
  version?: number;
  payload: TaskPayload;
  change_note: string;
  created_at: string;
  created_by?: number;
  created_by_kind?: string;
  created_by_id?: number;
}
export interface Review {
  id: number;
  revision_id: number;
  decision: string;
  note: string;
  created_at: string;
}
export interface Page<T> {
  execution_counts?: Record<string, number>;
  items: T[];
  total: number;
  page?: number;
  page_size?: number;
}
export interface Notification {
  id: number;
  title: string;
  body: string;
  kind: string;
  portal_task_id?: number;
  task_id?: number;
  read_at: string | null;
  created_at: string;
}
export const statusLabels: Record<TaskStatus, string> = {
  draft: "草稿",
  pending_review: "待审核",
  approved: "已通过",
  rejected: "已驳回",
  paused: "已暂停",
  offline: "已下架",
};
export const typeLabels: Record<TaskType, string> = {
  normal: "普通任务",
  other_normal: "其他普通任务",
  download: "下载任务",
  message_send: "iMessage 任务",
};
/** Format integer minor units without floating-point division losing cents. */
export const money = (minor: number | null | undefined) => {
  if (minor == null || !Number.isSafeInteger(minor)) return "—";
  const digits = String(Math.abs(minor)).padStart(3, "0");
  const whole = digits.slice(0, -2).replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  return `${minor < 0 ? "-" : ""}¥${whole}.${digits.slice(-2)}`;
};
export const integer = (value: number | null | undefined) =>
  value == null ? "—" : new Intl.NumberFormat("zh-CN").format(value);
export const percent = (
  numerator: number | null | undefined,
  denominator: number | null | undefined,
) =>
  numerator == null || denominator == null || denominator === 0
    ? "—"
    : `${((numerator / denominator) * 100).toFixed(1)}%`;
export const dateTime = (value: string | null | undefined) =>
  value
    ? new Intl.DateTimeFormat("zh-CN", {
        timeZone: "Asia/Shanghai",
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
        hour12: false,
      }).format(new Date(value))
    : "—";
export function assertTask(payload: TaskPayload) {
 if (payload.steps.some(s => (s.videos?.length || 0) > 1)) throw new Error("每步最多 1 个视频");
  if (!payload.title.trim()) throw new Error("请填写任务名称");
  if (payload.market_id !== "CN" || payload.currency !== "CNY")
    throw new Error("当前任务的市场或币种配置不受支持");
  if (
    !Number.isSafeInteger(payload.reward_amount_minor) ||
    payload.reward_amount_minor <= 0
  )
    throw new Error("单次奖励须大于 0，最多保留两位小数");
  if (!Number.isSafeInteger(payload.max_assignments) || payload.max_assignments <= 0)
    throw new Error("任务名额须为正整数");
  if (!Number.isSafeInteger(payload.reward_amount_minor * payload.max_assignments))
    throw new Error("任务预算超出允许范围，请减少名额或单次奖励");
  if (
    ["normal", "other_normal"].includes(payload.task_type) &&
    (!Number.isInteger(payload.completion_screenshot_count) || payload.completion_screenshot_count < 0 ||
    payload.completion_screenshot_count > 6)
  )
    throw new Error("完成截图数量必须为 0 至 6，0 表示沿用平台提交流程");
  if (
    payload.assignment_timeout_seconds < 0 ||
    payload.assignment_timeout_seconds > 604800
  )
    throw new Error("领取时限须在 0 至 7 天内，0 表示不启用超时");
  if (
    ["download", "message_send"].includes(payload.task_type) &&
    payload.platform !== "ios"
  )
    throw new Error("下载和消息任务仅支持 iOS 平台");
  if (
    payload.start_at &&
    payload.end_at &&
    new Date(payload.end_at) <= new Date(payload.start_at)
  )
    throw new Error("结束时间必须晚于开始时间");
  if (
    payload.steps.some((s) => !s.title.trim() || !s.description.trim())
  )
    throw new Error("请完整填写已添加的任务步骤");
  if (
    payload.required_screenshots?.length &&
    payload.required_screenshots.length !== payload.completion_screenshot_count
  )
    throw new Error("截图要求条数须与完成截图数量一致");
  if (payload.task_type === "normal" && payload.download_app) {
    const app = payload.download_app;
    if (!Number.isSafeInteger(app.id) || app.id <= 0 || app.search_keywords?.length !== 1 || !app.search_keywords[0]?.trim() || [...app.search_keywords[0].trim()].length > 100)
      throw new Error("使用领取时访问应用时，须填写有效的 App Store ID 和一个访问搜索词");
  }
  if (
    payload.task_type === "download" &&
    payload.download_app &&
    (!payload.download_app.search_keyword_quotas.length ||
      new Set(payload.download_app.search_keyword_quotas.map(q => q.keyword.trim().toLocaleLowerCase())).size !== payload.download_app.search_keyword_quotas.length ||
      payload.download_app.search_keyword_quotas.some(
        (q) => !q.keyword.trim() || !Number.isSafeInteger(q.target_count) || q.target_count < 1,
      ) ||
      payload.download_app.search_keyword_quotas.reduce(
        (sum, q) => sum + q.target_count,
        0,
      ) !== payload.max_assignments)
  )
    throw new Error("关键词须唯一且配额为正整数，配额合计必须等于任务数量");
  if (
    payload.task_type === "download" &&
    (!Number.isSafeInteger(payload.download_app?.id) || (payload.download_app?.id || 0) <= 0 || !payload.download_app?.bundle_id?.trim() || !payload.download_app?.name?.trim())
  )
    throw new Error("请填写有效的 App Store ID、Bundle ID 和应用名称");
  if (
    payload.task_type === "message_send" &&
    !payload.message_config?.manual_approval_required
  )
    throw new Error("消息任务需要平台人工审核");
  return payload;
}

/** Published ended tasks cannot be reopened or change their funded quota. */
export function isTaskClosed(task: Task): boolean {
  // The payload is the latest draft, not the published execution snapshot.
  // Older responses still expose server-owned terminal states; never infer
  // closure from the draft deadline or the browser clock.
  return !!task.formal_task_id && (task.formal_closed ??
    (task.status === "offline" || ["ended", "settled"].includes(task.execution_status || "")));
}
